import { api } from './api';

const MOBILE_REGEX = /^[+]?[1-9][0-9]{7,14}$/;

export const validateMobileNumber = (mobile) => {
    return MOBILE_REGEX.test(mobile);
};

export const searchCustomerByMobile = async (searchData) => {
    // Validate that at least one search parameter is provided
    const { mobile_number, policy_number, vehicle_registration_number } = searchData;

    if (!mobile_number && !policy_number && !vehicle_registration_number) {
        throw new Error('Please provide at least one search parameter: Mobile Number, Policy Number, or Vehicle Registration Number.');
    }

    // Validate mobile number if provided
    if (mobile_number && !validateMobileNumber(mobile_number)) {
        throw new Error('Please enter a valid mobile number (e.g. +919876543210).');
    }

    // client_scheme_id is optional - can search by unique fields (mobile_number, policy_number, vehicle_registration_number) without it

    return await api.post('/api/customers/search', searchData);
};

export const createCustomerWithPolicy = async (payload) => {
    if (!payload?.mobile_number) {
        throw new Error('Mobile number is required to create a customer.');
    }

    // Customer rows are client-independent; the client is derived from the
    // selected scheme on the policy (and from the scheme picked at case time).
    if (!payload?.is_paid_customer && !payload?.policy?.client_scheme_id) {
        throw new Error('Client scheme selection is required to create a customer.');
    }

    return await api.post('/api/customers/', payload);
};

/**
 * Fetch cases for a customer
 * @param {number} customerId - The ID of the customer
 * @param {Object} options - Optional parameters
 * @param {string} [options.status] - Filter cases by status
 * @param {number} [options.page=1] - Page number for pagination
 * @param {number} [options.limit=20] - Number of items per page
 * @returns {Promise<Object>} Object containing cases and pagination info
 */
export const getCustomerCases = async (customerId, options = {}) => {
    if (!customerId) {
        throw new Error('Customer ID is required');
    }

    const { status, page = 1, limit = 20 } = options;

    const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
    });

    if (status) {
        params.append('status', status);
    }

    return await api.get(`/api/customers/${customerId}/cases?${params.toString()}`);
};

/**
 * Fetch services with case details for a customer
 * @param {number} customerId - The ID of the customer
 * @param {Object} options - Optional parameters
 * @param {string} [options.status_filter] - Filter by service status
 * @param {string} [options.case_status_filter] - Filter by case status
 * @param {string} [options.service_type] - Filter by service type
 * @param {string} [options.start_date] - Filter services created after this date
 * @param {string} [options.end_date] - Filter services created before this date
 * @param {number} [options.page=1] - Page number for pagination
 * @param {number} [options.limit=20] - Number of items per page
 * @returns {Promise<Object>} Object containing services, customer info, and summary
 */
export const getCustomerServicesWithCases = async (customerId, options = {}) => {
    if (!customerId) {
        throw new Error('Customer ID is required');
    }

    const {
        status_filter,
        case_status_filter,
        service_type,
        start_date,
        end_date,
        page = 1,
        limit = 20
    } = options;

    const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
    });

    if (status_filter) {
        params.append('status_filter', status_filter);
    }
    if (case_status_filter) {
        params.append('case_status_filter', case_status_filter);
    }
    if (service_type) {
        params.append('service_type', service_type);
    }
    if (start_date) {
        params.append('start_date', start_date);
    }
    if (end_date) {
        params.append('end_date', end_date);
    }

    return await api.get(`/api/customers/${customerId}/services-with-cases?${params.toString()}`);
};

/**
 * Get available clients
 * @param {Object} options - Optional parameters
 * @param {boolean} [options.is_active=true] - Filter by active status
 * @returns {Promise<Object>} Object containing clients list
 */
export const getClients = async (options = {}) => {
    const { is_active = true } = options;
    const params = new URLSearchParams();

    if (is_active !== undefined) {
        params.append('is_active', is_active.toString());
    }

    return await api.get(`/api/cases/clients/?${params.toString()}`);
};

/**
 * Get available schemes for a client
 * @param {number} clientId - The ID of the client
 * @param {Object} options - Optional parameters
 * @param {boolean} [options.is_active=true] - Filter by active status
 * @returns {Promise<Object>} Object containing schemes list
 */
export const getClientSchemes = async (clientId, options = {}) => {
    if (!clientId) {
        throw new Error('Client ID is required');
    }

    const { is_active = true } = options;
    const params = new URLSearchParams();

    if (is_active !== undefined) {
        params.append('is_active', is_active.toString());
    }

    return await api.get(`/api/cases/clients/${clientId}/schemes/?${params.toString()}`);
};

/**
 * Graylist a customer
 * @param {number} customerId - The ID of the customer
 * @param {string} [reason] - Optional reason for graylisting
 * @returns {Promise<Object>} Response from the API
 */
export const graylistCustomer = async (customerId, reason = null) => {
    if (!customerId) {
        throw new Error('Customer ID is required');
    }

    const payload = {};
    if (reason) {
        payload.reason = reason;
    }

    return await api.put(`/api/customers/${customerId}/graylist`, payload);
};

/**
 * Reactivate a graylisted customer
 * @param {number} customerId - The ID of the customer
 * @param {string} [reason] - Optional reason for reactivation
 * @returns {Promise<Object>} Response from the API
 */
export const reactivateCustomer = async (customerId, reason = null) => {
    if (!customerId) {
        throw new Error('Customer ID is required');
    }

    const payload = {};
    if (reason) {
        payload.reason = reason;
    }

    return await api.put(`/api/customers/${customerId}/reactivate`, payload);
}; 